import * as angular from 'angular';
import * as _ from 'lodash';

import { IValueViewModel, SeriesSelectorViewModel } from '../../models/viewModels';
import { IRequestService } from '../request.service';
import { IIdentifierVmService } from '../vm/identifierVm.service';
import { ObjectType, SelectorType, KeyValuePair, IdentifierQuery, IValueTypeSelector, IGuid } from '../../models';

import {
    reconciledValues,
    reconciledConstraints,
} from '../../common/constants';
import { IValueTypeHelperService } from '../../services/helpers';
import { IIdentifierQueryService } from './identifierQuery.service';

export interface SeriesUniqueId {
    guid: string;
    value: string;
}

export interface ISeriesSelectorService {
    getSeriesSelectorViewModels(excludeTags?: boolean, includeQueries?: boolean): angular.IPromise<SeriesSelectorViewModel[]>;
    getQueriesSeriesSelectorViewModels(): angular.IPromise<SeriesSelectorViewModel[]>;
    getViewModels(guidList: string[]): angular.IPromise<SeriesSelectorViewModel[]>;
    getViewModel(guid: string): angular.IPromise<SeriesSelectorViewModel>;
    getValues(vm: IValueTypeSelector, orderBySelect?: boolean, isQuery?: boolean): KeyValuePair<string, string>[];
    getAvailableValueType(svm: IValueTypeSelector & IGuid, existings: SeriesUniqueId[], old?: SeriesUniqueId): string;
    getAvailableValueTypeFromQuery(svm: SeriesSelectorViewModel, existings: SeriesUniqueId[], old: SeriesUniqueId): string
    cleanCache(): void;
}

export class NewSeriesSelectorService implements ISeriesSelectorService {
    static serviceId = 'seriesSelectorService';

    static $inject = [
        'identifierVmService',
        'identifierQueryService',
        'requestService',
        'valueTypeHelperService'
    ];

    constructor(
        private _identifierVmService: IIdentifierVmService,
        private _identifierQueryService: IIdentifierQueryService,
        private _requestService: IRequestService,
        private _valueTypeHelper: IValueTypeHelperService
    ) {
    }

    getSeriesSelectorViewModels(excludeTags?: boolean): angular.IPromise<SeriesSelectorViewModel[]> {
        return this._identifierVmService
            .getAllViewModels()
            .then(data => {
                let result = data.map(d => this.convert(d));
                if (!!excludeTags) {
                    result = result.filter(r => r.type !== SelectorType.tagDefinition);
                }
                return result;
            });
    }

    getQueriesSeriesSelectorViewModels(): angular.IPromise<SeriesSelectorViewModel[]> {
        return this._identifierQueryService.getAll()
            .then(data => {
                const result = data.map(d => this.convertQuery(d.query));
                return result;
            });
    }

    getViewModels(guidList: string[]): angular.IPromise<SeriesSelectorViewModel[]> {
        return this._identifierVmService.getSomeViewModels(guidList).then(data => {
            const result = data.map(d => this.convert(d));
            return result;
        });
    }

    getViewModel(guid: string): angular.IPromise<SeriesSelectorViewModel> {
        return this._requestService.getAtV1('viewModel', { guid: guid }).then((data: IValueViewModel) => {
            const result = this.convert(data);
            return result;
        });
    }

    cleanCache(): void {
        this._requestService.cleanCacheAtV1('viewModel/all');
    }


    getValues(vm: IValueTypeSelector, orderBySelect: boolean = false, isQuery: boolean = false): KeyValuePair<string, string>[] {
        let result = this._valueTypeHelper.getViewModelValueTypes(vm, isQuery);

        if (vm.hasReconciledValue) {
            result = orderBySelect
                ? reconciledValues.concat(result)
                : result.concat(reconciledValues);
        } else if (vm.isConstraint) {
            result = orderBySelect
                ? reconciledConstraints.concat(result)
                : result.concat(reconciledConstraints);
        }

        return result;
    }

    getAvailableValueType(svm: SeriesSelectorViewModel, existings: SeriesUniqueId[], old: SeriesUniqueId = { guid: '', value: '' }): string {
        const existing = existings
            .filter(s => s.guid === svm.guid && !(s.guid === old.guid && s.value === old.value))
            .map(s => s.value);

        let values = this.getValues(svm, true)
            .map(s => s.key);

        values = _.difference(values, existing);

        return (_.includes(values, old.value))
            ? old.value
            : values[0];
    }

    getAvailableValueTypeFromQuery(svm: SeriesSelectorViewModel, existings: SeriesUniqueId[], old: SeriesUniqueId = { guid: '', value: '' }): string {
        const existing = existings
            .filter(s => s.guid === svm.guid && !(s.guid === old.guid && s.value === old.value))
            .map(s => s.value);

        let values = this.getValues(svm, true)
            .map(s => s.key);

        values = _.difference(values, existing);

        return (_.includes(values, old.value))
            ? old.value
            : values[0];
    }

    private convertQuery(d: IdentifierQuery): SeriesSelectorViewModel {
        const svm: SeriesSelectorViewModel = d as any;
        svm.type = SelectorType.query;
        return svm;
    }

    private convert(d: IValueViewModel): SeriesSelectorViewModel {
        const svm: SeriesSelectorViewModel = {
            acquisitionStrategy: d.acquisitionStrategy,
            aggregationMode: d.aggregationMode,
            comment: d.shortComment,
            connectionPointName: d.connectionPointName,
            dataType: d.actualDataType,
            groupAbsolutePath: d.groupAbsolutePath,
            groupList: d.groupList,
            groupUniqueName: d.groupUniqueName,
            guid: d.guid,
            hasReconciledValue: d.hasReconciledValue,
            identifierNames: d.identifierNames,
            identifierType: d.identifierType,
            isBoundToGroup: false,
            isConstraint: d.isConstraint,
            linkedGuid: '',
            linkedType: d.linkedType,
            longComment: d.longComment,
            max: d.max,
            min: d.min,
            name: d.name,
            objectType: d.objectType,
            selectionStrategy: d.selectionStrategy,
            shortComment: d.shortComment,
            tagDefinitionGuid: d.tagGuid,
            tagName: d.tagName,
            target: d.target,
            type: d.objectType === ObjectType.Tag
                ? SelectorType.tagDefinition
                : d.objectType === ObjectType.HistorianTag
                    ? SelectorType.historianTag
                    : SelectorType.identifier,
            unitName: d.unitName,
            variableTypeName: d.variableTypeName,
        };
        return svm;
    }
}